import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import '../styles/Projects.css';
import { apiFetch } from '../utils/api';
import ProjectCategories from '../constants/projectCategories';

function Projects() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const selectedCategory = searchParams.get('category') || 'all';
  const selectedSort = searchParams.get('sort') || null;
  const selectedStatus = searchParams.get('status') || 'all';

  const categories = [
    { value: 'all', label: 'All Projects' },
    ...ProjectCategories.OPTIONS.map(cat => ({ value: cat, label: cat }))
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch projects
        const projectsData = await apiFetch('/api/projects');
        setProjects(projectsData.projects || projectsData || []);

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCategoryChange = (category) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (category === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', category);
    }
    
    setSearchParams(newParams);
  };

  const handleSortChange = (sortType) => {
    const newParams = new URLSearchParams(searchParams);
    
    // Toggle off if same sort is selected
    if (selectedSort === sortType) {
      newParams.delete('sort');
    } else {
      newParams.set('sort', sortType);
    }
    
    setSearchParams(newParams);
  };

  const handleStatusChange = (status) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (status === 'all') {
      newParams.delete('status');
    } else {
      newParams.set('status', status);
    }
    
    setSearchParams(newParams);
  };

  const handleProjectClick = async (projectName) => {
    // Track the view in background (don't await)
    apiFetch(`/api/projects/${projectName}/view`, { method: 'POST' })
      .catch(err => {
        console.error('View tracking failed (non-blocking):', err);
      });
  };

  const filteredProjects = (() => {
    // First filter by category
    let filtered = selectedCategory === 'all' 
      ? projects 
      : projects.filter(project => project.category === selectedCategory);
    
    // Then filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(project => {
        if (selectedStatus === 'active') {
          return project.status === 'Active';
        } else if (selectedStatus === 'archived') {
          return project.status === 'Completed' || project.status === 'Archived' || project.status === 'On Hold';
        }
        return true;
      });
    }
    
    // Then apply sorting
    if (selectedSort === 'newest') {
      filtered = [...filtered].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (selectedSort === 'oldest') {
      filtered = [...filtered].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    }
    
    return filtered;
  })();

  if (loading) return <div className="loading">Loading projects...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="projects-page">
      <Helmet>
        <title>NHHS Analytics | Projects</title>
      </Helmet>
      {/* Hero Section */}
      <section className="projects-hero">
        <div className="projects-hero-content">
          <h1>Our Projects</h1>
          <p>
            Explore current and past analytics projects at NHHS. See what we're building, 
            learning, and exploring — and get inspired to join or contribute.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">{projects.filter(p => p.status === 'Active').length}</div>
              <div className="stat-label">Active Projects</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{projects.length}</div>
              <div className="stat-label">Total Projects</div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="filter-section">
          <div className="filters-container">
            <div className="filter-buttons">
              {categories.map(category => (
                <button
                  key={category.value}
                  className={`filter-btn ${selectedCategory === category.value ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category.value)}
                >
                  {category.label}
                </button>
              ))}
            </div>
            
            <div className="project-count">
              {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          <div className="controls-row">
            <div className="sort-buttons">
              <button
                className={`sort-btn ${selectedSort === 'newest' ? 'active' : ''}`}
                onClick={() => handleSortChange('newest')}
              >
                Newest
              </button>
              <button
                className={`sort-btn ${selectedSort === 'oldest' ? 'active' : ''}`}
                onClick={() => handleSortChange('oldest')}
              >
                Oldest
              </button>
            </div>
            
            <div className="status-buttons">
              <button
                className={`status-btn ${selectedStatus === 'all' ? 'active' : ''}`}
                onClick={() => handleStatusChange('all')}
              >
                All Status
              </button>
              <button
                className={`status-btn ${selectedStatus === 'active' ? 'active' : ''}`}
                onClick={() => handleStatusChange('active')}
              >
                Active
              </button>
              <button
                className={`status-btn ${selectedStatus === 'archived' ? 'active' : ''}`}
                onClick={() => handleStatusChange('archived')}
              >
                Archived
              </button>
            </div>
          </div>
        </div>

        <div className="projects-grid">
          {filteredProjects.map(project => (
            <Link 
              key={project._id} 
              className="project-card"
              to={`/projects/${project.name}`}
              onClick={() => handleProjectClick(project.name)}
            >
              <div className="project-image-container">
                <img 
                  src={project.image} 
                  alt={project.displayName || project.name} 
                  className="project-image"
                />
                <div className="project-status-badge">
                  {project.status || 'Active'}
                </div>
              </div>
              <div className="project-content">
                <div className="project-header">
                  <h3>{project.displayName || project.name}</h3>
                  <span className="project-category">{project.category}</span>
                </div>
                <p className="project-description">
                  {project.introduction || project.description || 'No description available'}
                </p>
                <div className="project-footer">
                  <span className="view-project">View Project →</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredProjects.length === 0 && !loading && (
          <div className="no-projects">
            <p>No projects found for the selected filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Projects;